import { Config } from '../../config';
import { Form } from '../form';
import { FormTypes } from '../form-types';
import { IUpSellResponse, IUpSellCallbacks, IUpSellChangeEvent, IUpSellEvent, IUpSellOrderItem, IUpSellSubmissionModel } from '../../config/upsell-response';
import { UpSellView } from '../../view/upsell/upsell-view';
import { Product } from '../../config/product';
import { Subject } from 'rxjs';
import { ProductType } from '../../config/product-type';

export class UpSellForm extends Form {

    type = FormTypes.UpSell;
    upSellResponse: IUpSellResponse = {};
    upSellProducts: Product[] = [];
    view: UpSellView = new UpSellView();

    upSellCallbacks: IUpSellCallbacks = {};
    upSellViewSubject: Subject<any> = new Subject();

    selectedProducts: any[] = [];

    orderItems: IUpSellOrderItem[] = [];

    constructor() {
        super();
        this.upSellCallbacks = {
            onChange: this.onUpSellChange.bind(this),
            onPurchaseAction: this.onUpSellPurchase.bind(this),
            onSkipAction: this.onUpSellSkip.bind(this),
            onProductQuantityAction: this.onProductQuantityChange.bind(this)
        };
    }


    async init(config: Config, callback: any, subject: Subject<IUpSellEvent>) {
        await super.init(config, callback);
        this.parentSubject = subject;

        this.upSellResponse = config.widget.data.result.upsell;
        this.upSellProducts = this.upSellResponse.products || [];

        this.listenToChanges();
        Promise.resolve();
    }

    listenToChanges() {
        if (this.parentSubject) {
            this.parentSubject.subscribe(res => {
                if (res) {
                    switch (res.evType) {
                        case IUpSellChangeEvent.QuantityChange:
                            this.onProductQuantityChange(res).then(x => this.upSellViewSubject.next(this.updateTotals()));
                            break;
                        case IUpSellChangeEvent.SelectChange:
                            this.onUpSellChange(res).then(x => this.upSellViewSubject.next(this.updateTotals()));
                            break;
                        default:
                            break;
                    }
                }
            });
        }
    }

    render() {
        if (!this.upSellResponse.products) {
            return Promise.reject();
        }

        this.view.render(this.upSellResponse, this.config.settings.currency, this.upSellCallbacks, this.upSellViewSubject);
        return Promise.resolve();
    }

    onUpSellChange(changeEvent: { product: Product, checked?: boolean, prodType: string }): Promise<void> {
        if (changeEvent.product.type === ProductType.DONATION) {
            const checkIfDonationAdded = this.selectedProducts.find(x => x.product.type === ProductType.DONATION && x.product.name === changeEvent.product.name);
            if (checkIfDonationAdded) {
                this.selectedProducts[this.selectedProducts.indexOf(checkIfDonationAdded)] = changeEvent;
                return Promise.resolve();
            }

            this.selectedProducts.push(changeEvent);
            return Promise.resolve();
        }

        const checkIfAdded = this.selectedProducts.find(x => x.product.type === changeEvent.product.type && x.product.name === changeEvent.product.name);

        if (checkIfAdded) {
            if (!changeEvent.checked) {
                this.selectedProducts = this.selectedProducts.filter(x => x.product.name !== changeEvent.product.name);
                return Promise.resolve();
            }
            this.selectedProducts[this.selectedProducts.indexOf(checkIfAdded)] = changeEvent;
            return Promise.resolve();
        }

        this.selectedProducts.push(changeEvent);
        return Promise.resolve();
    }

    onProductQuantityChange(changeEvent: { product: Product, checked?: boolean, quantity: string }): Promise<void> {
        const checkIfAdded = this.selectedProducts.find(x => x.product.type === changeEvent.product.type && x.product.name === changeEvent.product.name);
        if (!checkIfAdded) {
            return Promise.reject();
        }

        checkIfAdded.quantity = changeEvent.quantity;
        return Promise.resolve();

    }

    onUpSellPurchase(puchaseEvent: any) {
        this.config.widget.view.startProgress();
        this.updateOrderItems();
        this.createSubmissionData().then(data => {
            const submissionConfig = {
                host: process.env.API_URL,
                portalId: this.config.user.portalId
            };
            this.config.widget.refreshRecaptchaToken();
            this.config.widget.provider
                .submitWidget(submissionConfig, data, data.paymentMethod.type)
                .then(async (response: any) => {
                    if (response.recaptcha.requiresAction) {
                        await this.config.widget.handleRecaptcha(response, this.onUpSellPurchase.bind(this));
                        return;
                    }
                    this.config.widget.view.stopProgress();
                    this.renderUpSellThankMessage();
                });
        });

    }

    onUpSellSkip(skipEvent: any) {
        this.renderUpSellThankMessage();
    }

    renderUpSellThankMessage() {
        this.config.widget.showUpSellThankMessage();
    }

    updateOrderItems(): Promise<void> {
        this.orderItems = [];
        this.selectedProducts.forEach(selected => {
            switch (selected.product.type) {
                case ProductType.DONATION:
                    this.orderItems.push({
                        paymentType: ProductType.DONATION.toUpperCase(),
                        productName: selected.product.name,
                        amount: selected.amount,
                        currency: this.config.settings.currency,
                        tax: this.config.settings.tax || null,
                        discountId: null,
                        quantity: 1,
                        dynamicPlanInterval: selected.frequency,
                        subscriptionPlanId: null,
                        couponId: null,
                        trialDays: null
                    });
                    break;

                case ProductType.SINGLE_PAYMENT:
                    this.orderItems.push({
                        paymentType: ProductType.SINGLE_PAYMENT.toUpperCase(),
                        productName: selected.product.name,
                        amount: selected.product.amount,
                        currency: this.config.settings.currency,
                        tax: this.config.settings.tax || null,
                        discountId: null,
                        quantity: selected.quantity,
                        dynamicPlanInterval: null,
                        subscriptionPlanId: null,
                        couponId: null,
                        trialDays: null
                    });
                    break;

                case ProductType.SUBSCRIPTION:
                    this.orderItems.push({
                        paymentType: ProductType.SUBSCRIPTION.toUpperCase(),
                        productName: selected.product.name,
                        amount: selected.product.amount,
                        currency: this.config.settings.currency,
                        tax: this.config.settings.tax || null,
                        discountId: null,
                        quantity: selected.quantity,
                        dynamicPlanInterval: null,
                        subscriptionPlanId: selected.product.id,
                        couponId: null,
                        trialDays: null
                    });
                    break;

                default:
                    break;
            }
        });
        return Promise.resolve();
    }


    createSubmissionData(): Promise<IUpSellSubmissionModel> {
        const submissionData = this.config.widget.data as IUpSellSubmissionModel;

        submissionData.orderItems = this.orderItems;
        submissionData.paymentMethod.token = null;
        submissionData.paymentMethod.paymentMethodId = null;
        submissionData.orderId = submissionData.result.orderId;
        submissionData.upsellToken = submissionData.result.upsell.token;

        return Promise.resolve(submissionData);
    }

    updateTotals() {
        let total = 0;

        this.selectedProducts.forEach(selected => {
            selected.product.type === ProductType.DONATION ? total += selected.amount : total += (selected.product.amount * (selected.quantity || 1));
        });

        if (this.config.settings.tax && this.config.settings.tax > 0) {
            total = total + this.toTwoDec(total / 100 * this.config.settings.tax);
        }

        return { total, currency: this.config.settings.currency, tax: this.config.settings.tax };
    }
}
